import { useCallback, useContext, useEffect, useState } from "react";
import { MyContext } from "../../context/MyContext";
import AirHeader from "../../components/AirHeader";
import { Back } from "../../assets/svgs/icons";
import { LogoDefault } from "../../assets/svgs/logos";
import useMyActivityView from "../myActivityV2/hooks/useMyActivityView";
import IcoDB from "../../assets/icons/IcoDB";
import { useNavigate } from "react-router-dom";
import { AppBuyListStyle } from "./style";
import IcoOrangeInfo from "../../assets/icons/IcoOrangeInfo";
import { SubscriptionApi } from "../subscription/utils";

interface PurchaseHistoryItem {
  orderId: string;
  dsOrderId: string;
  externalOrderId: string;
  productId: string;
  productName: string;
  statusCode: "active" | "canceled" | "payment_failed" | "expired" | "none";
  startDate: string;
  endDate: string;
  nextBillingDate: string | null;
  paymentMethod: string;
  paidAmount: number;
  gracePeriodEndDate: string | null;
  autoRenewing: boolean;
}

interface PurchaseHistoryResponse {
  userId: string;
  purchases: PurchaseHistoryItem[];
  currentPage: number;
  totalPages: number;
  totalElements: number;
}

const PAGE_SIZE = 5;

const toDate = (value?: string | null) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}.${month}.${day}`;
};

const toPrice = (value: number) => `${value.toLocaleString("ko-KR")}원`;

const paymentMethodLabel = (method: string) => {
  if (method === "GOOGLE_PLAY") return "Google Play";
  return method || "-";
};

const getStatusUi = (item: PurchaseHistoryItem) => {
  if (item.statusCode === "active") {
    if (item.paidAmount === 0) {
      return {
        badgeType: "badge",
        badgeClassName: "badge gradient",
        badgeText: "무료체험 중",
      };
    }
    return {
      badgeType: "badge",
      badgeClassName: "badge purple",
      badgeText: "이용중",
    };
  }

  if (item.statusCode === "payment_failed") {
    return {
      badgeType: "badge",
      badgeClassName: "badge orange",
      badgeText: "결제실패",
    };
  }

  if (item.statusCode === "expired") {
    return {
      badgeType: "badge",
      badgeClassName: "badge gray",
      badgeText: "만료",
    };
  }

  if (item.statusCode === "canceled") {
    return {
      badgeType: "badge",
      badgeClassName: "badge purple",
      badgeText: "이용중",
    };
  }

  return {
    badgeType: "outline",
    badgeClassName: "outline-badge",
    badgeText: "상태확인",
  };
};

const AppBuyList = () => {
  const navigate = useNavigate();
  const { isMobile, user } = useContext(MyContext);
  const { handleGoBack } = useMyActivityView();

  const [items, setItems] = useState<PurchaseHistoryItem[]>([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPurchaseHistory = useCallback(
    async (currentPage: number) => {
      if (!user?.id) {
        setItems([]);
        setTotalPages(1);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await SubscriptionApi.get(
          `/billing/purchase-history?userId=${encodeURIComponent(
            user.id,
          )}&page=${currentPage}&size=${PAGE_SIZE}`,
        );

        if (!response.ok) {
          throw new Error(`구매내역 조회 실패 (${response.status})`);
        }

        const result = (await response.json()) as PurchaseHistoryResponse;
        setItems(Array.isArray(result.purchases) ? result.purchases : []);
        setPage(
          typeof result.currentPage === "number" ? result.currentPage : 0,
        );
        setTotalPages(
          typeof result.totalPages === "number" && result.totalPages > 0
            ? result.totalPages
            : 1,
        );
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "구매내역을 불러오지 못했습니다.";
        setError(errorMessage);
        setItems([]);
      } finally {
        setLoading(false);
      }
    },
    [user?.id],
  );

  useEffect(() => {
    fetchPurchaseHistory(page);
  }, [fetchPurchaseHistory, page]);

  const handleOpenSubscriptionManagement = () => {
    const android = (window as any).Android;
    if (android?.openSubscriptionManagement) {
      android.openSubscriptionManagement();
      return;
    }

    alert("앱에서만 사용 가능합니다.");
  };

  const handleOpenPaymentHistory = () => {
    const android = (window as any).Android;
    if (android?.openPlayStorePaymentHistory) {
      android.openPlayStorePaymentHistory();
      return;
    }

    alert("앱에서만 사용 가능합니다.");
  };

  return (
    <div className="flex flex-row h-full w-full bg-white overflow-hidden">
      <div
        className={`flex-1 flex flex-col min-h-0 overflow-hidden relative force-h-56px bg-[#f7f7f7]`}
      >
        {isMobile ? (
          <AirHeader
            type="sub"
            title="이용 내역"
            rightIcon={<IcoDB />}
            onRightIconClick={() => navigate("/subscription")}
          />
        ) : (
          <header className="relative flex items-center justify-center bg-white w-full h-[64px] shrink-0">
            {/* Back Button */}
            <button
              onClick={handleGoBack}
              className="absolute left-0 top-1/2 -translate-y-1/2 p-4"
              aria-label="뒤로가기"
            >
              <Back className="w-6 h-6 text-[#212121]" />
            </button>

            {/* Logo */}
            <LogoDefault className="w-[100px] h-[25px]" />
          </header>
        )}
        <div className="content overflow-y-auto min-h-0">
          <AppBuyListStyle.Container>
            {/* 임시 조치 display none 처리 */}
            <div className="container-top-message hidden">
              구글 인앱 결제 내역만 표시됩니다. 그 외 구매 내역은 <br />
              <a
                href="https://my.dongascience.com"
                target="_blank"
                rel="noopener noreferrer"
                className="mr-1"
              >
                [동아사이언스 마이페이지]
              </a>
              <span>에서 확인해 주세요.</span>
            </div>
            {error && (
              <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                {error}
              </div>
            )}

            {loading ? (
              <div className="py-8 text-center text-gray-500">
                구매내역을 불러오는 중입니다...
              </div>
            ) : items.length === 0 ? (
              <div className="py-8 text-center text-gray-500">
                구매내역이 없습니다.
              </div>
            ) : (
              items.map((item, index) => {
                const statusUi = getStatusUi(item);
                const canManageSubscription =
                  item.statusCode === "active" ||
                  item.statusCode === "payment_failed";

                return (
                  <AppBuyListStyle.Card key={index}>
                    {statusUi.badgeType === "outline" ? (
                      <div className={statusUi.badgeClassName}>
                        {statusUi.badgeText}
                      </div>
                    ) : (
                      <div className={statusUi.badgeClassName}>
                        {statusUi.badgeText}
                      </div>
                    )}

                    <h3>{item.productName}</h3>

                    <div className="kv">
                      <span>이용기간</span>
                      <span>
                        {toDate(item.startDate)} ~ {toDate(item.endDate)}
                      </span>
                    </div>

                    {item.nextBillingDate &&
                      item.statusCode !== "payment_failed" &&
                      item.statusCode !== "canceled" &&
                      item.statusCode !== "expired" && (
                        <div className="kv purple">
                          <span>다음 결제일</span>
                          <span>{toDate(item.nextBillingDate)}</span>
                        </div>
                      )}

                    {item.statusCode === "payment_failed" && (
                      <div className="fail-msg">
                        <div className="fail-title">
                          <span>
                            <IcoOrangeInfo />
                          </span>
                          <span>결제 실패</span>
                        </div>
                        <div className="fail-desc">
                          구독 관리에서 결제 수단을 변경해주세요
                          <br />
                          결제 재시도 기한: ~{toDate(item.gracePeriodEndDate)}
                        </div>
                      </div>
                    )}

                    <div className="kv">
                      <span>결제처</span>
                      <span>{paymentMethodLabel(item.paymentMethod)}</span>
                    </div>
                    <div className="kv">
                      <span>주문번호</span>
                      <span>{item.dsOrderId}</span>
                    </div>
                    <div className="kv">
                      <span>구글 결제번호</span>
                      <span>{item.externalOrderId}</span>
                    </div>
                    <div className="price">{toPrice(item.paidAmount)}</div>
                    <div className="btns">
                      <button
                        className="outline-btn"
                        onClick={handleOpenPaymentHistory}
                      >
                        결제 내역 보기
                      </button>
                      {canManageSubscription && (
                        <button
                          className="primary-btn"
                          onClick={handleOpenSubscriptionManagement}
                        >
                          구독 관리
                        </button>
                      )}
                    </div>
                  </AppBuyListStyle.Card>
                );
              })
            )}

            <AppBuyListStyle.Pagination>
              <button
                className="prev-btn"
                onClick={() => setPage((prev) => Math.max(prev - 1, 0))}
                disabled={page <= 0 || loading}
              >
                <img
                  alt="Previous"
                  src="https://img.dongascience.com/air/prev.svg"
                />
              </button>
              <span className="current-page">{page + 1}</span>
              <span className="divider">/</span>
              <span className="total-page">{totalPages}</span>
              <button
                className="next-btn"
                onClick={() =>
                  setPage((prev) =>
                    Math.min(prev + 1, Math.max(totalPages - 1, 0)),
                  )
                }
                disabled={page >= totalPages - 1 || loading}
              >
                <img
                  alt="Next"
                  src="https://img.dongascience.com/air/next.svg"
                />
              </button>
            </AppBuyListStyle.Pagination>
          </AppBuyListStyle.Container>
        </div>
      </div>
    </div>
  );
};

export default AppBuyList;
